/**
 * Orientations — admin catalog view.
 *
 * Two lenses on the same site-scoped data:
 *
 *   - **By Orientation** (default): card grid of every orientation the
 *     coordinator can see, with assignees rendered as status-dotted
 *     chips. Lets you answer "who has X?".
 *   - **By User**: cards per user (only those with ≥1 active assignment
 *     on this site), listing each assignment with status (plus its length
 *     when it is a legacy timed one — single-length assignments have none)
 *     and an Unassign action. Lets you answer "what does Y have?" — and
 *     fix typos when a wrong user got picked in Orientation Assignment.
 *
 * Distinct from `Orientation Editor` (authoring surface) and
 * `My Orientations` (trainee-scoped assignment view). Unassign uses
 * `cancel_assignment` (server-side soft-cancel); Undo re-fires
 * `assign_orientation` with the snapshotted email + orientation +
 * version — the new row gets a fresh assignment_id, which is fine.
 */

import { useEffect, useMemo, useState } from 'react';
import { useSpacetimeDB } from 'spacetimedb/react';
import { type DbConnection as MinetwinConn } from '@sdk/minetwin';
import { useOrientations, type OrientationRow } from '@/hooks/useOrientations';
import { useOrientationStepOrder } from '@/hooks/useOrientationStepOrder';
import { useCohortProgress } from '@/hooks/useCohortProgress';
import { useUsers, type UserRow } from '@/hooks/useUsers';
import { useProjects } from '@/hooks/useProjects';
import { useTweaks } from '@/hooks/useTweaks';
import { NoProjects } from '@/components/NoProjects';
import { NoSiteMembership } from '@/components/NoSiteMembership';
import { useMySiteMembership } from '@/hooks/useMySiteMembership';
import { formatLegacyVersionTag } from '@/lib/orientationVersion';
import type { Site, Toast } from '@/data/types';
import type {
  AssignmentStatus,
  OrientationVersion,
} from '@/generated/minetwin/types';
import { GatedButton } from '@/components/GatedButton';

type AssigneeBrief = {
  assignmentId: bigint;
  email: string;
  displayName: string;
  status: AssignmentStatus['tag'];
};

// Per-user view needs more than the chip carries: we keep orientationId
// + orientationVersion + projectId on each row so Unassign's Undo closure
// can re-fire `assign_orientation` with the exact same shape Cohort
// Builder uses, without re-deriving anything from the row that just
// disappeared.
type AssignmentDetail = {
  assignmentId: bigint;
  email: string;
  orientationId: string;
  projectId: string;
  orientationVersion: OrientationVersion;
  status: AssignmentStatus['tag'];
};

type OrientationsProps = { toast: (t: Omit<Toast, 'id'>) => void };

type ViewMode = 'by-orientation' | 'by-user';

// CSS Grid `auto-fill, minmax(MIN_CARD_WIDTH_PX, 1fr)` — gives 1 column
// on phones (<= ~330 viewport once gutters are accounted), 2 columns on
// tablets, 3+ on desktops without manual breakpoints. 280 is tuned so the
// title + status pill + length-line fit on one line at minimum width.
const MIN_CARD_WIDTH_PX = 280;

// Cards per page. 12 = 4 rows on a 3-column desktop, 6 rows on a
// 2-column tablet, 12 stacked on a phone. Tweak together with
// MIN_CARD_WIDTH_PX above when re-sizing the cards.
const PAGE_SIZE = 12;

type StatusFilter = 'All' | 'Active' | 'Draft' | 'Archived';
const STATUS_FILTERS: StatusFilter[] = ['All', 'Active', 'Draft', 'Archived'];


export function Orientations({ toast }: OrientationsProps) {
  const { sites, isReady: sitesReady } = useProjects();
  const { tweaks } = useTweaks();
  const currentSite: Site | undefined = sites[tweaks.siteIndex] ?? sites[0];
  const projectId = currentSite?.projectName ?? '';
  const membership = useMySiteMembership(projectId);
  const { orientations, isReady: orientationsReady } = useOrientations(
    currentSite?.projectName,
  );
  const { stepsByOrientation, isReady: stepsReady } = useOrientationStepOrder();
  // Admin-visible `cohort_progress` is server-scoped via `admin_project_ids`,
  // so OrgAdmin/Admin callers see every assignment across the projects they
  // can see. Joined to `useUsers` for display names — falls back to email
  // when the user hasn't signed in yet (pending) so newly-invited assignees
  // still show up by email.
  const { rows: assignments, isReady: assignmentsReady } = useCohortProgress();
  const { users, isReady: usersReady } = useUsers();
  const minetwinState = useSpacetimeDB('minetwin');

  const [view, setView] = useState<ViewMode>('by-orientation');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('All');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  // Locally hide rows the user just clicked Unassign on, so the row
  // disappears immediately instead of waiting for the server roundtrip
  // to remove it from `cohort_progress`. Once the server commits, the
  // row drops out of `assignments` and the entry can be cleared.
  const [pendingCancels, setPendingCancels] = useState<Set<bigint>>(
    () => new Set(),
  );

  const isReady =
    sitesReady && orientationsReady && stepsReady && assignmentsReady && usersReady;

  const userByEmail = useMemo(() => {
    const m = new Map<string, UserRow>();
    for (const u of users) m.set(u.email.toLowerCase(), u);
    return m;
  }, [users]);

  // Lookup `orientationId → displayName` for the by-user view's row labels.
  // Same pattern ReviewQueue uses to resolve completed-assignment names.
  const orientationNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const o of orientations) m.set(o.orientationId, o.displayName);
    return m;
  }, [orientations]);

  // Assignments-by-orientation map, scoped to the current site. Orientations
  // are org-wide but assignments carry their own `projectId`, so we filter
  // by `currentSite.projectName` to keep the card surface consistent with
  // the rest of the page (which is already site-scoped via useOrientations).
  // Sort within each list so InProgress floats above NotStarted/Completed —
  // that's the status a coordinator is most likely to need to act on.
  // Cancelled rows and rows the user just clicked Unassign on (still
  // pending server-confirm) are hidden — matches LiveSessions / ReviewQueue
  // conventions and makes the optimistic unassign feel instant.
  const assigneesByOrientation = useMemo(() => {
    const m = new Map<string, AssigneeBrief[]>();
    for (const a of assignments) {
      if (projectId && a.projectId !== projectId) continue;
      if (a.status.tag === 'Cancelled') continue;
      if (pendingCancels.has(a.assignmentId)) continue;
      const u = userByEmail.get(a.email.toLowerCase());
      const list = m.get(a.orientationId) ?? [];
      list.push({
        assignmentId: a.assignmentId,
        email: a.email,
        displayName: u?.displayName ?? a.email,
        status: a.status.tag,
      });
      m.set(a.orientationId, list);
    }
    for (const list of m.values()) {
      list.sort((x, y) => statusRank(x.status) - statusRank(y.status));
    }
    return m;
  }, [assignments, userByEmail, projectId, pendingCancels]);

  // Assignments grouped by email (the stable cross-state key) for the
  // by-user view. Same site + status filters as above. Each entry holds
  // everything Undo needs to re-fire assign_orientation, since the row
  // disappears from `assignments` once the server confirms the cancel.
  const assignmentsByUserEmail = useMemo(() => {
    const m = new Map<string, AssignmentDetail[]>();
    for (const a of assignments) {
      if (projectId && a.projectId !== projectId) continue;
      if (a.status.tag === 'Cancelled') continue;
      if (pendingCancels.has(a.assignmentId)) continue;
      const key = a.email.toLowerCase();
      const list = m.get(key) ?? [];
      list.push({
        assignmentId: a.assignmentId,
        email: a.email,
        orientationId: a.orientationId,
        projectId: a.projectId,
        orientationVersion: a.orientationVersion,
        status: a.status.tag,
      });
      m.set(key, list);
    }
    for (const list of m.values()) {
      list.sort((x, y) => statusRank(x.status) - statusRank(y.status));
    }
    return m;
  }, [assignments, projectId, pendingCancels]);

  // Sorted list of users with ≥1 active assignment on the current site.
  // Pending users (no UserRow) still appear — their displayName falls
  // back to email so a typo'd assignment to an unregistered address is
  // still recoverable. Search applies across displayName + email.
  const userCards = useMemo(() => {
    const q = search.trim().toLowerCase();
    const rows: { key: string; email: string; displayName: string;
      appRole: UserRow['appRole'] | null; assignments: AssignmentDetail[] }[] = [];
    for (const [emailKey, list] of assignmentsByUserEmail.entries()) {
      const u = userByEmail.get(emailKey);
      const email = list[0]?.email ?? emailKey;
      const displayName = u?.displayName ?? email;
      if (q) {
        const hay = `${displayName} ${email}`.toLowerCase();
        if (!hay.includes(q)) continue;
      }
      rows.push({
        key: emailKey,
        email,
        displayName,
        appRole: u?.appRole ?? null,
        assignments: list,
      });
    }
    rows.sort((a, b) => a.displayName.localeCompare(b.displayName));
    return rows;
  }, [assignmentsByUserEmail, userByEmail, search]);

  // Unassign with optimistic hide + Undo toast. The reducer fires
  // synchronously on the local SDK; the row leaves `cohort_progress`
  // when the server commits, which clears the pending flag naturally
  // on the next memo recompute. On dispatch error we restore the row
  // and surface the failure.
  const handleUnassign = (detail: AssignmentDetail, orientationName: string) => {
    const conn = minetwinState.getConnection() as MinetwinConn | null;
    if (!conn) {
      toast({ title: 'Not connected to MineTwin module', kind: 'bad' });
      return;
    }
    setPendingCancels((prev) => {
      const next = new Set(prev);
      next.add(detail.assignmentId);
      return next;
    });
    try {
      conn.reducers.cancelAssignment({ assignmentId: detail.assignmentId });
    } catch (err) {
      setPendingCancels((prev) => {
        const next = new Set(prev);
        next.delete(detail.assignmentId);
        return next;
      });
      const msg = err instanceof Error ? err.message : String(err);
      toast({ title: 'Unassign failed', sub: msg, kind: 'bad' });
      return;
    }
    const userLabel =
      userByEmail.get(detail.email.toLowerCase())?.displayName ?? detail.email;
    toast({
      title: `Unassigned ${orientationName}`,
      sub: `from ${userLabel}`,
      kind: 'info',
      undo: () => {
        const undoConn = minetwinState.getConnection() as MinetwinConn | null;
        if (!undoConn) {
          toast({ title: 'Not connected to MineTwin module', kind: 'bad' });
          return;
        }
        try {
          // Re-fire with the snapshotted fields. The server assigns a fresh
          // assignment_id; the user-visible effect is identical (orientation
          // + email + version restored).
          undoConn.reducers.assignOrientation({
            email: detail.email,
            orientationId: detail.orientationId,
            projectId: detail.projectId,
            orientationVersion: detail.orientationVersion,
          });
          toast({
            title: `Re-assigned ${orientationName}`,
            sub: `to ${userLabel}`,
            kind: 'good',
          });
        } catch (err) {
          const msg = err instanceof Error ? err.message : String(err);
          toast({ title: 'Re-assign failed', sub: msg, kind: 'bad' });
        }
      },
    });
  };

  const filtered = useMemo<OrientationRow[]>(() => {
    const q = search.trim().toLowerCase();
    return orientations.filter((o) => {
      if (statusFilter !== 'All' && o.status.tag !== statusFilter) return false;
      if (!q) return true;
      if (o.displayName.toLowerCase().includes(q)) return true;
      if (o.description.toLowerCase().includes(q)) return true;
      if (o.occupationTarget.some((t) => t.toLowerCase().includes(q))) return true;
      return false;
    });
  }, [orientations, statusFilter, search]);

  // Pagination — recompute totals against whichever lens is active so
  // the same page-state drives both views. Resets to page 1 on any
  // filter/search/view change so the user always sees the first match.
  const activeCount =
    view === 'by-orientation' ? filtered.length : userCards.length;
  const totalPages = Math.max(1, Math.ceil(activeCount / PAGE_SIZE));
  useEffect(() => {
    if (page > totalPages) setPage(1);
  }, [page, totalPages]);
  useEffect(() => {
    setPage(1);
  }, [statusFilter, search, view]);

  const pageStart = (page - 1) * PAGE_SIZE;
  const orientationPageItems = filtered.slice(pageStart, pageStart + PAGE_SIZE);
  const userPageItems = userCards.slice(pageStart, pageStart + PAGE_SIZE);
  const showingFrom = activeCount === 0 ? 0 : pageStart + 1;
  const showingTo = Math.min(pageStart + PAGE_SIZE, activeCount);

  if (sitesReady && sites.length === 0) {
    return <NoProjects pageName="Orientations" />;
  }
  if (membership.isReady && currentSite && !membership.isMember)
    return <NoSiteMembership pageName="Orientations" siteName={currentSite.name} />;

  return (
    <>
      <div className="page-head">
        <div>
          <h1 style={{ margin: 0, fontSize: 18 }}>Orientations</h1>
          <div style={{ color: 'var(--muted)', fontSize: 12, marginTop: 2 }}>
            {currentSite ? (
              <>
                {currentSite.name}
                {' · '}
                {orientationsReady
                  ? `${orientations.length} orientation${orientations.length === 1 ? '' : 's'}`
                  : 'loading…'}
              </>
            ) : (
              'No site selected'
            )}
          </div>
        </div>
        <ViewToggle view={view} onChange={(v) => { setView(v); setPage(1); }} />
      </div>

      <div className="page-body">
        <div
          style={{
            display: 'flex',
            gap: 10,
            alignItems: 'center',
            flexWrap: 'wrap',
            marginBottom: 12,
          }}
        >
          <input
            type="search"
            placeholder={
              view === 'by-orientation'
                ? 'Search by name, description, or occupation…'
                : 'Search by name or email…'
            }
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              flex: '1 1 240px',
              minWidth: 200,
              padding: '6px 10px',
              fontSize: 13,
              background: 'var(--bg-1)',
              border: '1px solid var(--border)',
              borderRadius: 6,
              color: 'var(--text)',
            }}
          />
          {view === 'by-orientation' ? (
            <div style={{ display: 'flex', gap: 4 }}>
              {STATUS_FILTERS.map((s) => {
                const active = statusFilter === s;
                return (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setStatusFilter(s)}
                    style={{
                      padding: '5px 12px',
                      fontSize: 12,
                      fontWeight: 600,
                      background: active ? 'var(--accent)' : 'var(--bg-1)',
                      color: active ? 'var(--accent-fg)' : 'var(--muted)',
                      border: `1px solid ${active ? 'var(--accent)' : 'var(--border)'}`,
                      borderRadius: 6,
                      cursor: 'pointer',
                    }}
                  >
                    {s}
                  </button>
                );
              })}
            </div>
          ) : null}
        </div>

        {!isReady ? (
          <div
            style={{
              padding: 22,
              textAlign: 'center',
              color: 'var(--muted)',
              fontSize: 13,
            }}
          >
            Loading…
          </div>
        ) : activeCount === 0 ? (
          <div
            style={{
              padding: 22,
              textAlign: 'center',
              color: 'var(--muted)',
              fontSize: 13,
            }}
          >
            {view === 'by-orientation'
              ? orientations.length === 0
                ? `No orientations defined for ${currentSite?.name ?? 'this site'} yet.`
                : 'No orientations match the current filter.'
              : search.trim()
                ? 'No users match the current search.'
                : `No assignments yet on ${currentSite?.name ?? 'this site'}.`}
          </div>
        ) : (
          <>
            {/* Auto-adjusting card grid. `auto-fill` + `minmax(MIN, 1fr)`
              packs as many columns as fit the container at the card's
              minimum width; widens cells past MIN once a row can't
              gain another column. Works phones-to-desktop without a
              media query. */}
            <div
              style={{
                display: 'grid',
                gap: 12,
                gridTemplateColumns: `repeat(auto-fill, minmax(${MIN_CARD_WIDTH_PX}px, 1fr))`,
              }}
            >
              {view === 'by-orientation'
                ? orientationPageItems.map((o) => {
                    const steps = stepsByOrientation.get(o.orientationId) ?? [];
                    const assignees =
                      assigneesByOrientation.get(o.orientationId) ?? [];
                    return (
                      <OrientationCard
                        key={o.orientationId}
                        orientation={o}
                        stepCount={steps.length}
                        assignees={assignees}
                      />
                    );
                  })
                : userPageItems.map((u) => (
                    <UserAssignmentsCard
                      key={u.key}
                      displayName={u.displayName}
                      email={u.email}
                      appRole={u.appRole}
                      assignments={u.assignments}
                      orientationNameById={orientationNameById}
                      onUnassign={handleUnassign}
                    />
                  ))}
            </div>

            {/* Pagination strip — wraps onto two rows on phones via the
              flexWrap, so the counter stays readable even when the
              button group can't fit on one line. */}
            <div
              style={{
                marginTop: 14,
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 8,
                color: 'var(--muted)',
                fontSize: 12.5,
              }}
            >
              <div>
                Showing <span style={{ color: 'var(--text)', fontWeight: 600 }}>{showingFrom}</span>
                {'–'}
                <span style={{ color: 'var(--text)', fontWeight: 600 }}>{showingTo}</span>
                {' of '}
                <span style={{ color: 'var(--text)', fontWeight: 600 }}>{activeCount}</span>
              </div>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <PageButton
                  label="Prev"
                  reason={page <= 1 ? 'Already on the first page' : null}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                />
                <span style={{ padding: '0 6px', fontWeight: 600, color: 'var(--text)' }}>
                  {page} / {totalPages}
                </span>
                <PageButton
                  label="Next"
                  reason={page >= totalPages ? 'Already on the last page' : null}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                />
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}

function ViewToggle({
  view,
  onChange,
}: {
  view: ViewMode;
  onChange: (v: ViewMode) => void;
}) {
  const tabs: { key: ViewMode; label: string }[] = [
    { key: 'by-orientation', label: 'By orientation' },
    { key: 'by-user', label: 'By user' },
  ];
  return (
    <div
      style={{
        display: 'inline-flex',
        gap: 0,
        border: '1px solid var(--border)',
        borderRadius: 6,
        overflow: 'hidden',
        background: 'var(--bg-1)',
      }}
    >
      {tabs.map((t, i) => {
        const active = view === t.key;
        return (
          <button
            key={t.key}
            type="button"
            onClick={() => onChange(t.key)}
            style={{
              padding: '6px 14px',
              fontSize: 12,
              fontWeight: 600,
              // `--accent` / `--accent-fg` aren't defined anywhere in
              // styles.css — the active tab was rendering transparent
              // with muted text, indistinguishable from inactive. Swap
              // to the themed amber palette other surfaces use (matches
              // the `.chip-filter.on` look).
              background: active ? 'var(--amber-soft)' : 'transparent',
              color: active ? 'var(--amber)' : 'var(--muted)',
              border: 'none',
              borderLeft: i === 0 ? 'none' : '1px solid var(--border)',
              cursor: 'pointer',
            }}
          >
            {t.label}
          </button>
        );
      })}
    </div>
  );
}

function UserAssignmentsCard({
  displayName,
  email,
  appRole,
  assignments,
  orientationNameById,
  onUnassign,
}: {
  displayName: string;
  email: string;
  appRole: UserRow['appRole'] | null;
  assignments: AssignmentDetail[];
  orientationNameById: Map<string, string>;
  onUnassign: (detail: AssignmentDetail, orientationName: string) => void;
}) {
  return (
    <div
      style={{
        border: '1px solid var(--border)',
        borderRadius: 8,
        padding: 12,
        background: 'var(--panel-2)',
        display: 'grid',
        gap: 8,
        // `minmax(0, 1fr)` is the load-bearing fix — without it, the
        // implicit grid column defaults to `auto` (= max-content), so
        // the single column inside grows to fit the widest assignment
        // row's natural width (long orientation name + chip + Unassign
        // button ≈ 340-360px), which then exceeds the outer card cell
        // and gets clipped by `overflow: hidden`. With `minmax(0, 1fr)`
        // the inner column shrinks to its parent (card content area)
        // and the ellipsisable orientation name absorbs the overflow.
        gridTemplateColumns: 'minmax(0, 1fr)',
        // See OrientationCard — `minWidth: 0` lets the grid cell hold
        // the column width when the inner email/name has no break
        // opportunity, and `overflow: hidden` clips the worst-case
        // single token that beats the inner ellipsis (e.g. a 60-char
        // unhyphenated assignment id).
        minWidth: 0,
        overflow: 'hidden',
      }}
    >
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontWeight: 600,
              fontSize: 14,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {displayName}
          </div>
          {displayName !== email ? (
            <div
              style={{
                color: 'var(--muted)',
                fontSize: 11,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {email}
            </div>
          ) : null}
        </div>
        {appRole ? <RoleBadge role={appRole.tag} /> : null}
      </div>
      <div
        style={{
          fontSize: 11,
          color: 'var(--muted)',
          fontWeight: 600,
          borderTop: '1px dashed var(--border)',
          paddingTop: 8,
        }}
      >
        {assignments.length} assignment{assignments.length === 1 ? '' : 's'}
      </div>
      <div
        style={{
          display: 'grid',
          gap: 6,
          // Same fix as the outer card — without this, the implicit
          // grid column auto-sizes to max-content of the widest
          // assignment row (long orientation name + chip + Unassign)
          // and overflows the card boundary.
          gridTemplateColumns: 'minmax(0, 1fr)',
        }}
      >
        {assignments.map((a) => {
          const name = orientationNameById.get(a.orientationId) ?? a.orientationId;
          const palette =
            ASSIGNEE_STATUS[a.status] ?? ASSIGNEE_STATUS.NotStarted;
          return (
            <div
              key={String(a.assignmentId)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '6px 8px',
                background: 'var(--bg-1)',
                border: '1px solid var(--border)',
                borderRadius: 6,
              }}
            >
              <span
                title={palette.label}
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  background: palette.dot,
                  flexShrink: 0,
                }}
              />
              <div
                style={{
                  flex: 1,
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  fontSize: 12.5,
                }}
                title={name}
              >
                {name}
              </div>
              {/* Single-length mode: `Default` has no length to show. A legacy
                  timed assignment keeps its badge until it is re-assigned. */}
              {formatLegacyVersionTag(a.orientationVersion.tag) && (
                <span
                  style={{
                    fontSize: 10.5,
                    padding: '1px 6px',
                    borderRadius: 8,
                    background: 'var(--panel-3)',
                    color: 'var(--muted)',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                  }}
                >
                  {formatLegacyVersionTag(a.orientationVersion.tag)}
                </span>
              )}
              <button
                type="button"
                onClick={() => onUnassign(a, name)}
                className="unassign-btn"
                // Same reason as the row's chip + RoleBadge — the
                // ellipsisable orientation name (flex:1, min-width:0)
                // gives up width first; the Unassign button stays
                // intact so a coordinator can always reach it.
                style={{ flexShrink: 0 }}
              >
                Unassign
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function RoleBadge({ role }: { role: string }) {
  const palette: Record<string, { bg: string; fg: string }> = {
    OrgAdmin: { bg: 'var(--good-soft)', fg: 'var(--good)' },
    OrgViewer: { bg: 'var(--warn-soft)', fg: 'var(--warn)' },
    Admin: { bg: 'var(--good-soft)', fg: 'var(--good)' },
    Member: { bg: 'var(--panel-3)', fg: 'var(--muted)' },
    Contractor: { bg: 'var(--panel-3)', fg: 'var(--muted)' },
  };
  const s = palette[role] ?? palette.Member;
  return (
    <span
      style={{
        fontSize: 10,
        padding: '2px 8px',
        borderRadius: 10,
        background: s.bg,
        color: s.fg,
        fontWeight: 600,
        whiteSpace: 'nowrap',
        // Don't let flex pressure squash the badge — the name flex:1
        // sibling should give up width first. Without this, the badge
        // would shrink below "OrgAdmin"'s text width and the trailing
        // characters would get clipped by the card's `overflow: hidden`.
        flexShrink: 0,
      }}
    >
      {role}
    </span>
  );
}

function PageButton({
  label,
  reason,
  onClick,
}: {
  label: string;
  reason: string | null;
  onClick: () => void;
}) {
  const disabled = reason !== null;
  return (
    <GatedButton
      onClick={onClick}
      reason={reason}
      style={{
        padding: '5px 12px',
        fontSize: 12,
        fontWeight: 600,
        background: 'var(--bg-1)',
        color: disabled ? 'var(--muted)' : 'var(--text)',
        border: '1px solid var(--border)',
        borderRadius: 6,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.55 : 1,
      }}
    >
      {label}
    </GatedButton>
  );
}

function OccupationChips({ occupations }: { occupations: string[] }) {
  if (occupations.length === 0) {
    return <span style={{ color: 'var(--muted)' }}>Universal</span>;
  }
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
      {occupations.slice(0, 3).map((t) => (
        <span
          key={t}
          style={{
            fontSize: 11,
            padding: '1px 8px',
            background: 'var(--bg-1)',
            border: '1px solid var(--border)',
            borderRadius: 10,
            color: 'var(--text)',
          }}
        >
          {t}
        </span>
      ))}
      {occupations.length > 3 ? (
        <span
          style={{ fontSize: 11, color: 'var(--muted)' }}
          title={occupations.slice(3).join(', ')}
        >
          +{occupations.length - 3}
        </span>
      ) : null}
    </div>
  );
}

function OrientationCard({
  orientation: o,
  stepCount,
  assignees,
}: {
  orientation: OrientationRow;
  stepCount: number;
  assignees: AssigneeBrief[];
}) {
  return (
    <div
      style={{
        border: '1px solid var(--border)',
        borderRadius: 8,
        padding: 12,
        background: 'var(--panel-2)',
        display: 'grid',
        gap: 8,
        // Same rationale as UserAssignmentsCard — the implicit grid
        // column defaults to max-content, so the OccupationChips row
        // or the AssigneeList chip flex-wrap row can drive the card
        // wider than its outer cell. `minmax(0, 1fr)` keeps the
        // single column at the parent's width.
        gridTemplateColumns: 'minmax(0, 1fr)',
        // Without `minWidth: 0`, an unbreakable token inside (a long
        // unspaced displayName / a URL in the description) forces the
        // grid cell to grow past its 1fr slot and overflows the card
        // boundary. `overflow: hidden` is the safety net that clips
        // anything an inner `overflow-wrap` didn't catch (e.g. an inline
        // <img> or pill that's individually wider than the card).
        minWidth: 0,
        overflow: 'hidden',
      }}
    >
      {/* Title row — name (wraps if long) + status pill aligned right.
        `min-width: 0` on the title cell is the standard fix for flex
        children of a row that need to truncate / wrap without forcing
        the row wider than its parent. */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
        <div
          style={{
            flex: 1,
            minWidth: 0,
            fontWeight: 600,
            fontSize: 14,
            // Wrap long unspaced tokens (the default `break-word` only
            // breaks at hyphens / soft-wrap opportunities; `anywhere`
            // breaks mid-word as a last resort so a 60-char id doesn't
            // push the row past the card).
            overflowWrap: 'anywhere',
          }}
        >
          {o.displayName}
        </div>
        <StatusPill status={o.status.tag} />
      </div>

      {o.description ? (
        <div
          style={{
            color: 'var(--muted)',
            fontSize: 12,
            lineHeight: 1.4,
            overflowWrap: 'anywhere',
          }}
        >
          {o.description}
        </div>
      ) : null}

      {/* Stats row — uses dot separators rather than separate label/value
        pairs to keep the vertical footprint tight on small screens. */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '4px 10px',
          fontSize: 11.5,
          color: 'var(--muted)',
        }}
      >
        <span>
          <span style={{ color: 'var(--text)', fontWeight: 600 }}>{stepCount}</span>{' '}
          step{stepCount === 1 ? '' : 's'}
        </span>
        <span>·</span>
        <span>
          Auto-assign{' '}
          <span
            style={{
              color: o.autoAssign ? 'var(--good)' : 'var(--muted)',
              fontWeight: 600,
            }}
          >
            {o.autoAssign ? 'on' : 'off'}
          </span>
        </span>
      </div>

      <OccupationChips occupations={o.occupationTarget} />

      <AssigneeList assignees={assignees} />
    </div>
  );
}

/** InProgress floats above NotStarted/Completed/Cancelled in card lists —
 *  that's the bucket a coordinator is most likely to need to act on. */
function statusRank(tag: AssignmentStatus['tag']): number {
  switch (tag) {
    case 'InProgress':
      return 0;
    case 'NotStarted':
      return 1;
    case 'Completed':
      return 2;
    case 'Cancelled':
      return 3;
    default:
      return 4;
  }
}

const ASSIGNEE_PREVIEW = 4;

function AssigneeList({ assignees }: { assignees: AssigneeBrief[] }) {
  const [expanded, setExpanded] = useState(false);
  if (assignees.length === 0) {
    return (
      <div
        style={{
          fontSize: 11,
          color: 'var(--muted)',
          borderTop: '1px dashed var(--border)',
          paddingTop: 8,
        }}
      >
        No assignees yet
      </div>
    );
  }
  const visible = expanded ? assignees : assignees.slice(0, ASSIGNEE_PREVIEW);
  const hidden = assignees.length - visible.length;
  return (
    <div
      style={{
        borderTop: '1px dashed var(--border)',
        paddingTop: 8,
        display: 'grid',
        gap: 6,
      }}
    >
      <div style={{ fontSize: 11, color: 'var(--muted)', fontWeight: 600 }}>
        Assignees{' '}
        <span style={{ color: 'var(--text)' }}>{assignees.length}</span>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
        {visible.map((a) => (
          <AssigneeChip key={String(a.assignmentId)} assignee={a} />
        ))}
        {hidden > 0 ? (
          <button
            type="button"
            onClick={() => setExpanded(true)}
            style={{
              fontSize: 11,
              padding: '1px 8px',
              background: 'transparent',
              border: '1px dashed var(--border)',
              borderRadius: 10,
              color: 'var(--muted)',
              cursor: 'pointer',
            }}
          >
            +{hidden} more
          </button>
        ) : null}
        {expanded && assignees.length > ASSIGNEE_PREVIEW ? (
          <button
            type="button"
            onClick={() => setExpanded(false)}
            style={{
              fontSize: 11,
              padding: '1px 8px',
              background: 'transparent',
              border: '1px dashed var(--border)',
              borderRadius: 10,
              color: 'var(--muted)',
              cursor: 'pointer',
            }}
          >
            Show less
          </button>
        ) : null}
      </div>
    </div>
  );
}

function AssigneeChip({ assignee }: { assignee: AssigneeBrief }) {
  const palette = ASSIGNEE_STATUS[assignee.status] ?? ASSIGNEE_STATUS.NotStarted;
  return (
    <span
      title={`${assignee.email} — ${palette.label}`}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        fontSize: 11,
        padding: '2px 8px',
        background: 'var(--bg-1)',
        border: '1px solid var(--border)',
        borderRadius: 10,
        color: 'var(--text)',
        maxWidth: 160,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}
    >
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          background: palette.dot,
          flexShrink: 0,
        }}
      />
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {assignee.displayName}
      </span>
    </span>
  );
}

const ASSIGNEE_STATUS: Record<
  string,
  { label: string; dot: string }
> = {
  NotStarted: { label: 'Not started', dot: 'var(--muted-2)' },
  InProgress: { label: 'In progress', dot: 'var(--warn)' },
  Completed: { label: 'Completed', dot: 'var(--good)' },
  Cancelled: { label: 'Cancelled', dot: 'var(--bad)' },
};

function StatusPill({ status }: { status: string }) {
  const styles: Record<string, { bg: string; fg: string; label: string }> = {
    Active: { bg: 'var(--good-soft)', fg: 'var(--good)', label: 'Active' },
    Draft: { bg: 'var(--warn-soft)', fg: 'var(--warn)', label: 'Draft' },
    Archived: { bg: 'var(--panel-3)', fg: 'var(--muted)', label: 'Archived' },
  };
  const s = styles[status] ?? styles.Draft;
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 10px',
        borderRadius: 10,
        background: s.bg,
        color: s.fg,
        fontSize: 11,
        fontWeight: 600,
        whiteSpace: 'nowrap',
        // OrientationCard renders this in a flex title row next to a
        // flex:1 displayName. Without flexShrink:0 the pill compresses
        // below its text width and "Archived"/"Active"/"Draft" loses
        // its trailing characters when the card is narrow.
        flexShrink: 0,
      }}
    >
      {s.label}
    </span>
  );
}
